import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    const { dialog } = this.refs;
    if (dialog) {
      dialog.addEventListener('cancel', this.#handleDialogCancel);
      dialog.addEventListener('close', this.#handleNativeDialogClose);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    const { dialog } = this.refs;
    if (dialog) {
      dialog.removeEventListener('cancel', this.#handleDialogCancel);
      dialog.removeEventListener('close', this.#handleNativeDialogClose);
    }

    this.#unlockPageScroll({ restoreScrollPosition: false });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // Guard against double-open calls queued in the same frame.
      if (dialog.open) return;

      /*
       * Dialogs that opt in with the `scroll-lock` attribute must also set `html[scroll-lock]`.
       * Details elements get this via the global `toggle` listener below; `<dialog>` opened
       * here did not, so themes (e.g. custom header scroll freeze, overflow: hidden) never ran.
       * Cart drawer / search modal then left scroll handlers active during body lock → wrong
       * scrollTop-derived header state and layout vars while the drawer is open.
       */
      if (dialog.hasAttribute('scroll-lock')) {
        document.documentElement.setAttribute('scroll-lock', '');
      }

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Clears fixed-body/page scroll lock styles.
   * @param {{ restoreScrollPosition?: boolean }} [options]
   */
  #unlockPageScroll(options = {}) {
    const { dialog } = this.refs;
    const restoreScrollPosition = options.restoreScrollPosition !== false;
    const shouldRestoreScroll =
      restoreScrollPosition && document.body.style.position === 'fixed' && document.body.style.top;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (dialog?.hasAttribute('scroll-lock')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    if (shouldRestoreScroll) {
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (this.#isClosing) return;

    if (!dialog.open) {
      // Dialog may already be closed by native ESC/cancel path; still release locks.
      this.#unlockPageScroll();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#isClosing = true;

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      this.#unlockPageScroll();

      dialog.close();
      dialog.classList.remove('dialog-closing');

      this.dispatchEvent(new DialogCloseEvent());
    } finally {
      this.#isClosing = false;
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Intercept native <dialog> ESC behavior so we always run our unlock/animation path.
   * @param {Event} event
   */
  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Fallback cleanup for native/third-party dialog.close() calls.
   */
  #handleNativeDialogClose = () => {
    if (this.#isClosing) return;
    this.#unlockPageScroll();
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
